/*
 * *****************************************************************************
 * File: home.js (/src/pages/home.js) | frontend
 * Written by Andrea Corsini <andrea@rs1.it>
 * =============================================================
 * Created on Sunday, 14th March 2021 5:21:16 pm
 *
 * Copyright (c) 2020-2021 Andrea Corsini T/A RS1 Project - All rights reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential.
 *
 * Modified on Thursday, 1st April 2021 11:22:38 am
 * *****************************************************************************
 */
import React, { useState, useRef, useEffect, useCallback } from 'react'
import styled from '@emotion/styled'
import {
    motion,
    AnimateSharedLayout,
    AnimatePresence,
    useAnimation,
} from 'framer-motion'

import { ReactComponent as GetOnAppStore } from '../assets/images/appstore.svg'

const servizi = [
    {
        id: 'libretto',
        name: 'Info Libretto',
        message: `L&apos;app numero <span class='bolder'>#1</span><br />in <span class='bolder'>Italia</span> per cercare<br />informazioni di <br /><span class='bolder'>auto</span> e <span class='bolder'>moto</span>`,
    },
    {
        id: 'patente',
        name: 'Punti Patente',
        message: `Punti e scadenza<br />della tua <span class='bolder'>patente</span>`,
    },
    {
        id: 'rca',
        name: 'RCA',
        message: `Informazioni dettagliate<br />sullo stato <span class='bolder'>assicurativo</span>`,
    },
    {
        id: 'revisione',
        name: 'Revisione',
        message: `Controlla quando &egrave;<br />prevista la <span class='bolder'>revisione</span>`,
    },
    {
        id: 'furto',
        name: 'Furto',
        message: `Determina se un<br />veicolo risulta <span class='bolder'>rubato</span>`,
    },
    {
        id: 'bollo',
        name: 'Bollo',
        message: `Verifica il costo del <span class='bolder'>bollo</span><br />in base alla regione`,
    },
    {
        id: 'neopatentati',
        name: 'Neopatentati',
        message: `Verifica se un veicolo &egrave;<br />adatto per <span class='bolder'>neopatentati</span>`,
    },
    {
        id: 'ambientale',
        name: 'Classe ambientale',
        message: `Controlla la <span class='bolder'>classe<br />ambientale</span> di un veicolo`,
    },
]
const serviziIds = servizi.map(({ id = '' }) => id)

const MotionImg = ({ src = '', webp = false, ...props }) => (
    <motion.picture>
        {webp && <source srcSet={webp} type='image/webp' />}
        <source srcSet={src} type='image/png' />
        <motion.img src={src} {...props} />
    </motion.picture>
)

const Page = () => {
    const [state, setState] = useState({ ...servizi[0] })
    const timerRef = useRef()
    const progressAnimation = useAnimation()

    const nextSlide = useCallback(() => {
        let index = serviziIds.indexOf(state.id) + 1
        if (index < 0 || index >= serviziIds.length) index = 0
        setState(s => ({ ...s, ...servizi[index] }))
    }, [state.id])

    useEffect(() => {
        const duration = 5.0
        progressAnimation.stop()
        timerRef.current = setTimeout(nextSlide, duration * 1000)
        progressAnimation.start({
            width: '100%',
            transition: { from: '0%', duration, ease: 'linear' },
        })
        return () => {
            if (timerRef.current) clearTimeout(timerRef.current)
        }
    }, [nextSlide, progressAnimation])

    const setSlide = id => {
        const index = serviziIds.indexOf(id)
        if (index >= 0) {
            setState(s => ({ ...s, ...servizi[index] }))
        }
    }

    return (
        <>
            <Slider>
                <Slogan>
                    {servizi.map(
                        ({ id, message }) =>
                            state.id === id && (
                                <motion.div
                                    key={id}
                                    initial={{ y: -300, opacity: 0 }}
                                    animate={{ y: 0, opacity: 1 }}
                                    dangerouslySetInnerHTML={{
                                        __html: message,
                                    }}
                                />
                            )
                    )}
                </Slogan>
                <Preview>
                    <AnimatePresence>
                        {servizi.map(
                            ({ id = 'libretto' }) =>
                                state.id === id && (
                                    <MotionImg
                                        key={`preview-${id}`}
                                        initial={{
                                            scale: 0,
                                            opacity: 0,
                                        }}
                                        animate={{ scale: 1, opacity: 1 }}
                                        exit={{
                                            scale: 1,
                                            opacity: 0,
                                        }}
                                        src={`/services/previews/${id}.png`}
                                        webp={`/services/previews/${id}.webp`}
                                        alt={`Informazioni veicolo ottenute con l'app Veicolo`}
                                    />
                                )
                        )}
                    </AnimatePresence>
                </Preview>
                <ProgressBar>
                    <motion.div
                        className='innerBar'
                        initial={{ width: '0%' }}
                        animate={progressAnimation}
                    />
                </ProgressBar>
            </Slider>
            <Funzioni>
                <AnimateSharedLayout>
                    {servizi.map(({ id = 'libretto', name = '' }) => (
                        <Service key={id} onClick={() => setSlide(id)}>
                            {state.id === id && (
                                <motion.div
                                    className='serviceSelector'
                                    initial={false}
                                    layoutId='background'
                                />
                            )}
                            <ServiceIcon>
                                <MotionImg
                                    src={`/services/icons/${id}.png`}
                                    webp={`/services/icons/${id}.webp`}
                                    alt={`Icona ${name}`}
                                />
                            </ServiceIcon>
                            <ServiceTitle>{name}</ServiceTitle>
                        </Service>
                    ))}
                </AnimateSharedLayout>
            </Funzioni>
            <Content>
                <Preview>
                    <MotionImg
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        src='/services/previews/garage.png'
                        webp='/services/previews/garage.webp'
                        alt={`Garage virtuale tramite l'app Veicolo`}
                    />
                </Preview>
                <Slogan>
                    <motion.div
                        initial={{ y: -300, opacity: 0 }}
                        animate={{ y: 0, opacity: 1 }}
                        dangerouslySetInnerHTML={{
                            __html: `Aggiungi qualsiasi veicolo al tuo <span class='bolder'>Garage</span>.<br /><br />Avrai sempre con te le informazioni del <span class='bolder'>libretto</span> e ti ricordermo tutte le <span class='bolder'>scadenze</span>, sia nell'app che in un comodo widget.`,
                        }}
                    />
                </Slogan>
                <Widget>
                    <MotionImg
                        initial={{ scale: 0.9, opacity: 0 }}
                        animate={{ scale: 1, opacity: 1 }}
                        src='/services/previews/widget.png'
                        webp='/services/previews/widget.webp'
                        alt={`Widget scadenze Garage virtuale tramite l'app Veicolo`}
                    />
                </Widget>
            </Content>
            <Stores>
                <a
                    href='https://apps.apple.com/it/app/id1099449684'
                    target='_blank'
                    rel='noreferrer'
                >
                    <GetOnAppStore />
                </a>
                <a
                    href='https://play.google.com/store/apps/details?id=it.talimac.veicolo&hl=it'
                    target='_blank'
                    rel='noreferrer'
                >
                    <img
                        src='/it_badge_web_generic.png'
                        alt='Disponibile su Google Play'
                    />
                </a>
            </Stores>
            <Copyright>
                App Store è un marchio di Apple Inc., Google Play e il logo di
                Google Play sono marchi di Google LLC.
            </Copyright>
        </>
    )
}
export default Page

const ProgressBar = styled.div`
    position: absolute;
    left: 1%;
    bottom: -26px;
    width: 98%;
    height: 2px;
    border-radius: 7px;
    background: #121212;
    & .innerBar {
        position: absolute;
        left: 0;
        top: 0;
        width: 50%;
        height: 100%;
        border-radius: 7px;
        background: #555555;
    }
`

const Preview = styled.div`
    & img {
        width: 100%;
    }
`
const Slider = styled.div`
    position: relative;
    display: grid;
    grid-template-columns: 1fr 2fr;
    min-height: 50vh;
    & ${Preview} {
        position: relative;
        width: 100%;
        padding-top: 78.77%;
        & img {
            position: absolute;
            top: 0;
            left: 0;
            width: 100%;
            height: 100%;
            object-fit: contain;
            object-position: center;
        }
    }
    @media (max-width: 1023px) {
        grid-template-columns: 1.5fr 2fr;
    }
    @media (max-width: 767px) {
        grid-template-columns: 1fr;
    }
`
const Slogan = styled.div`
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    justify-content: center;
    margin: 0;
    font-size: 35px;
    line-height: 1.2;
    font-weight: 300;
    margin-bottom: 100px;
    & .bolder {
        font-weight: 500;
    }
    @media (max-width: 1023px) {
        font-size: 25px;
    }
    @media (max-width: 767px) {
        margin-bottom: 25px;
    }
`

const Funzioni = styled.div`
    display: grid;
    grid-template-columns: repeat(4, 1fr);
    grid-template-rows: repeat(2, 1fr);
    margin-top: 50px;
    @media (max-width: 767px) {
        grid-template-columns: repeat(2, 1fr);
        grid-template-rows: repeat(4, 1fr);
    }
`

const Service = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    margin-bottom: 30px;
    cursor: pointer;
    transition: all 0.2s linear;
    position: relative;
    z-index: 0;
    &:hover {
        transform: scale(1.05);
    }
    & .serviceSelector {
        position: absolute;
        top: -10px;
        left: -10px;
        right: 15px;
        bottom: -10px;
        background: #121212;
        z-index: -1;
        border-radius: 7px;
    }
    @media (max-width: 767px) {
        flex-direction: column;
        justify-content: center;
        & .serviceSelector {
            left: 0px;
            right: 0px;
        }
    }
`

const ServiceIcon = styled.div`
    position: relative;
    flex-shrink: 0;
    width: 50px;
    height: 50px;
    & img {
        position: absolute;
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        object-fit: contain;
        object-position: center;
    }
`

const ServiceTitle = styled.div`
    margin: 0 15px;
    font-weight: 600;
    @media (max-width: 767px) {
        margin: 15px 15px 0;
        text-align: center;
    }
`

const Widget = styled.div`
    & img {
        width: 100%;
    }
`

const Content = styled.div`
    margin-top: 50px;
    display: grid;
    grid-template-columns: repeat(3, 1fr);
    grid-template-rows: repeat(4, 1fr);
    & ${Preview} {
        grid-column-start: 1;
        grid-column-end: 2;
        grid-row-start: 1;
        grid-row-end: 5;
    }
    & ${Slogan} {
        grid-column-start: 2;
        grid-column-end: 4;
        grid-row-start: 1;
        grid-row-end: 3;
        margin: 0 0 0 50px;
        font-size: 30px;
    }
    & ${Widget} {
        grid-column-start: 2;
        grid-column-end: 4;
        grid-row-start: 3;
        grid-row-end: 5;
        position: relative;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: center;
        & img {
            width: 325px;
            height: auto;
            object-fit: contain;
            object-position: center;
        }
    }
    @media (max-width: 1023px) {
        & ${Slogan} {
            font-size: 22px;
        }
        & ${Widget} {
            & img {
                width: 225px;
            }
        }
    }
    @media (max-width: 767px) {
        grid-template-columns: 1fr;
        grid-template-rows: auto auto auto;
        & ${Preview} {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 1;
            grid-row-end: 2;
        }
        & ${Slogan} {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 2;
            grid-row-end: 3;
            margin: 50px 0;
        }
        & ${Widget} {
            grid-column-start: 1;
            grid-column-end: 2;
            grid-row-start: 3;
            grid-row-end: 4;
            & img {
                width: 100%;
            }
        }
    }
`
const Stores = styled.div`
    display: flex;
    flex-direction: row;
    align-items: center;
    justify-content: center;
    margin-top: 50px;
    & a {
        margin: 0 25px;
    }
    & svg {
        width: 120px;
    }
    & img {
        width: 155px;
    }
`
const Copyright = styled.div`
    margin-top: 25px;
    font-size: 12px;
    color: #666666;
    text-align: center;
`
